import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, arraySwap, rectSwappingStrategy } from '@dnd-kit/sortable';
import { Cancel, Edit, Save, WarningAmber } from '@mui/icons-material';
import { Alert, Button, Grid, Typography } from '@mui/material';
import { isCompanyUser } from '@thro-helpers/users';
import { Tile, TilesPosition, CreateTile } from '@thro-types/users';

import { omit } from 'lodash';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useLayoutMutation, useLayoutQuery } from 'api/hooks/layout';
import { useTilesMutation } from 'api/hooks/tiles';
import { ConfirmationDialog } from 'app/components';
import { translations } from 'locales/i18n';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import { useUnloadListener } from 'utils/hooks/useUnloadListener';
import { useInjectReducer } from 'utils/redux-injectors';

import { TileType } from '..';
import { AlertActions } from '../../Alert/slice';
import { selectLayout, selectTileDrawer } from '../selectors';
import {
  LayoutActions,
  TileDrawerActions,
  layoutReducer,
  layoutSliceKey
} from '../tileDrawerSlice';
import { DrawerMode } from '../types';
import { AddCard } from './AddCard';
import { DashboardCard } from './DashboardCard';

export const DashboardContent = () => {
  const { t } = useTranslation();
  useInjectReducer({ key: layoutSliceKey, reducer: layoutReducer });

  const [editMode, setEditMode] = useState(false);
  const [deleteTileDialogOpen, setDeleteTileDialogOpen] = useState(false);
  const [cancelButtonDialogOpen, setCancelButtonDialogOpen] = useState(false);
  const [tileToDelete, setTileToDelete] = useState<Tile | undefined>(undefined);

  const { creation } = useTilesMutation();

  const localLayout = useSelector(selectLayout);
  const dispatch = useDispatch();
  const sensors = useSensors(useSensor(MouseSensor));
  const { customTilesToDelete } = useSelector(selectTileDrawer);
  const { user } = useAuthContext();

  const { data: layout, isLoading } = useLayoutQuery();
  const { creation: createLayout, update: updateLayout } = useLayoutMutation();
  const { deletion } = useTilesMutation();

  useUnloadListener({
    condition: editMode && localLayout.tilesPositions !== layout.layout?.tilesPositions
  });
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    function swap() {
      const oldIndex = localLayout.tilesPositions.findIndex(
        item => item.id.toString() === active.id
      );
      const newIndex = localLayout.tilesPositions.findIndex(
        item => item.id.toString() === over?.id
      );
      let newArr = arraySwap(localLayout.tilesPositions, oldIndex, newIndex);
      newArr = newArr.map((item, index) => ({
        ...item,
        position: index
      }));

      return newArr;
    }
    if (active.id !== over?.id) {
      dispatch(LayoutActions.setLayout(swap()));
    }
  }

  useEffect(() => {
    if (layout.layout?.tilesPositions) {
      dispatch(LayoutActions.setLayout(layout.layout.tilesPositions));
    }
  }, [dispatch, layout]);

  const handleRemoveClick = (tile: Tile) => {
    setTileToDelete(tile);
    setDeleteTileDialogOpen(true);
  };

  const handleRemoveMutation = async (tile: Tile) => {
    if (tile.type === TileType.DEFAULT || tile.id === 0) {
      dispatch(LayoutActions.removeTilePosition(tile.id));
    } else {
      try {
        // addTileToDeletionArray to mark it for Deletion
        dispatch(TileDrawerActions.addTileToCustomTilesToDelete(tile));
        dispatch(LayoutActions.removeTilePosition(tile.id));
        dispatch(AlertActions.success(t(translations.pages.home.alerts.successfulRemove())));
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.home.alerts.failedRemove())));
      }
    }
  };

  const handleSaveSystemTiles = async () => {
    try {
      if (customTilesToDelete.length !== 0) {
        customTilesToDelete.forEach(async tile => {
          await deletion.mutateAsync(tile.id);
        });
        dispatch(TileDrawerActions.setCustomTilesToDelete([]));
      }
      const updatedTilesPositions = await Promise.all(
        localLayout.tilesPositions.map(async tp => {
          if (tp.tile.id === 0) {
            const newTileWithoutId: CreateTile = omit(tp.tile, 'id');
            const tileInDatabase = await creation.mutateAsync(newTileWithoutId);
            return { ...tp, tile: tileInDatabase };
          }
          return tp;
        })
      );

      //Aktualisierung des Layouts.
      if (layout?.layout) {
        await updateLayout.mutateAsync({
          id: layout.layout.userId,
          data: {
            tilesPositions: updatedTilesPositions.map(tp => {
              return { tileId: tp.tile.id, position: tp.position };
            })
          }
        });
      } else {
        await createLayout.mutateAsync({
          tilesPositions: updatedTilesPositions.map(tp => {
            return { tileId: tp.tile.id, position: tp.position };
          })
        });
      }
    } catch (error) {
      dispatch(AlertActions.error(t(translations.pages.home.alerts.failedCreate())));
    } finally {
    }
  };

  const handleToggleEditMode = () => {
    if (!layout.layout?.tilesPositions) {
      const tilePositions: TilesPosition[] = layout.defaultTiles.map((tile, index) => {
        return { id: tile.id, position: index, tile: tile };
      });
      dispatch(LayoutActions.setLayout(tilePositions));
    }
    setEditMode(true);
  };

  const handleCancelEditMode = () => {
    if (layout.layout) {
      dispatch(LayoutActions.setLayout(layout.layout.tilesPositions));
    }
    dispatch(TileDrawerActions.setCustomTilesToDelete([]));

    if (!layout.layout?.tilesPositions) {
      dispatch(LayoutActions.setLayout([]));
    }

    setEditMode(false);
  };

  const renderSystemTiles = () => {
    return (
      <>
        {layout?.systemTiles.map((item, index) => (
          <DashboardCard id={item.id.toString()} key={item.id} tile={item} />
        ))}
      </>
    );
  };

  const renderLayout = useCallback(() => {
    const tiles: ReactNode[] = [];

    if (user && isCompanyUser(user)) {
      tiles.push(
        <DashboardCard
          key="company"
          id="company"
          //@ts-ignore
          tile={{
            icon: {
              id: 0,
              path: '',
              isRounded: false,
              isDefault: false
            },
            link: `/companies/${user.company?.id}`,
            title_de: user.company?.name || '',
            shortText_de: t(translations.pages.home.company.description())
          }}
        />
      );
    }

    if (localLayout.tilesPositions.length) {
      tiles.push(
        localLayout.tilesPositions.map((tp, index) => (
          <DashboardCard
            id={tp.id.toString()}
            key={tp.position}
            editMode={editMode}
            handleRemoveClick={handleRemoveClick}
            tile={tp.tile}
          />
        ))
      );
    }

    if (!layout) {
      return <></>;
    }
    if (!layout.layout) {
      tiles.push(
        layout.defaultTiles.map((item, index) => (
          <DashboardCard
            id={item.id.toString()}
            key={index}
            editMode={editMode}
            handleRemoveClick={handleRemoveClick}
            tile={item}
          />
        ))
      );
    }

    return tiles;
  }, [localLayout, layout, editMode, user, t]);

  const renderIndividualDashboard = () => {
    return (
      <DndContext sensors={sensors} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
        <SortableContext
          items={localLayout.tilesPositions.map(tp => tp.id.toString())}
          strategy={rectSwappingStrategy}
        >
          {renderLayout()}
          {editMode && (
            <AddCard
              key={'add'}
              onClick={() => {
                dispatch(TileDrawerActions.setCurrentMode(DrawerMode.PREDEFINED));
                dispatch(TileDrawerActions.open());
              }}
            />
          )}
        </SortableContext>
      </DndContext>
    );
  };

  return (
    <>
      <ConfirmationDialog
        open={deleteTileDialogOpen}
        title={t(translations.pages.home.dialog.deleteConfirm.title())}
        onClose={() => {
          setDeleteTileDialogOpen(false);
        }}
        onConfirm={() => {
          tileToDelete && handleRemoveMutation(tileToDelete);
          setDeleteTileDialogOpen(false);
        }}
      >
        <div>{t(translations.pages.home.dialog.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={cancelButtonDialogOpen}
        title={t(translations.pages.home.dialog.cancelEditMode.title())}
        onClose={() => {
          setCancelButtonDialogOpen(false);
        }}
        onConfirm={() => {
          handleCancelEditMode();
          setCancelButtonDialogOpen(false);
        }}
      >
        <div>{t(translations.pages.home.dialog.cancelEditMode.message())}</div>
      </ConfirmationDialog>
      {editMode && localLayout.tilesPositions !== layout.layout?.tilesPositions && (
        <Alert
          icon={<WarningAmber fontSize="inherit" />}
          style={{ marginTop: 15, marginBottom: 15 }}
          severity="info"
        >
          {t(translations.pages.home.notSaveMessage())}
        </Alert>
      )}
      <Grid container alignItems="flex-start" gap={2}>
        <Grid item xs>
          <Typography marginBottom={2} variant="h6" fontWeight={'bold'}>
            {t(translations.pages.home.systemTilesTitle())}
          </Typography>
        </Grid>
        {editMode ? (
          <Grid item>
            <Grid container gap={1} justifyContent={'flex-end'}>
              <Button
                variant="contained"
                startIcon={<Cancel />}
                style={{ backgroundColor: 'red', color: 'white' }}
                onClick={() => setCancelButtonDialogOpen(true)}
              >
                {t(translations.common.cancel())}
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                style={{ color: 'white' }}
                onClick={() => {
                  setEditMode(mode => !mode);
                  handleSaveSystemTiles();
                }}
              >
                {t(translations.common.save())}
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Grid item>
            <Grid container item justifyContent={'flex-end'}>
              <Button
                variant="contained"
                startIcon={<Edit />}
                style={{ color: 'white' }}
                onClick={() => handleToggleEditMode()}
              >
                {t(translations.pages.home.editMode())}
              </Button>
            </Grid>
          </Grid>
        )}
      </Grid>
      {layout?.systemTiles.length && !isLoading ? (
        <>
          <Grid container spacing={2}>
            {renderSystemTiles()}
          </Grid>
          <Typography marginY={2} variant="h6" fontWeight={'bold'}>
            {t(translations.pages.home.customTilesTitle())}
          </Typography>
        </>
      ) : (
        <></> // TODO Render Skeleton while loading
      )}

      <Grid container spacing={2}>
        {renderIndividualDashboard()}
      </Grid>
    </>
  );
};
